import React, { memo, useCallback, useEffect, useState, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { noop, omit } from 'lodash-es';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import classnames from 'classnames';
import { useResponseStream } from 'site-modules/canvas/hooks/use-response-stream';
import { CANVAS_CREATIVE_ID, PLACEHOLDER_IMAGE_SRC } from 'site-modules/canvas/constants/canvas';
import { ANIMATION_GAP, AnimationSchedule } from 'site-modules/canvas/components/animation-schedule/animation-schedule';
import { getAnimationUnitId } from 'site-modules/canvas/components/animation-schedule/markdown-scheduled-node';
import { EventToolbox } from 'client/utils/event-toolbox';
import { PAGE_ASYNC_CONTENT_LOAD } from 'client/tracking/constant';
import { WordByWordRenderer } from './word-by-word-renderer/word-by-word-renderer';
import { MarkdownLinkRenderer } from './markdown-link-renderer/markdown-link-renderer';
import { MarkdownImage } from './markdown-image/markdown-image';
import { MarkdownList, MarkdownListItem } from './markdown-list/markdown-list';
import { MarkdownTable } from './markdown-table/markdown-table';

import './markdown.scss';

const WORD_BY_WORD_TAGS = ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'em', 'strong', 'i', 'u', 'td', 'th', 'del', 'pre'];
const STRUCTURAL_TAGS = ['code', 'hr', 'input'];
const MARKDOWN_PLUGINS = [remarkGfm];

export function autoCloseMarkdown(text) {
  if (!text?.length) return '';

  // Image autoclose
  let imageAnalyzeText = text;

  const incompleteImageRegex = /!\[[^\]]*]?(?:\([^)]*)?$/; // ![... or ![...](...
  const match = text.match(incompleteImageRegex);

  if (match && !match[0].endsWith(')')) {
    imageAnalyzeText = text.replace(incompleteImageRegex, `![Loading...](${PLACEHOLDER_IMAGE_SRC})`);
  }

  // Bold/italic autoclose
  if (imageAnalyzeText.trim().endsWith('*')) {
    return imageAnalyzeText;
  }

  let closingSuffix = '';

  let boldItalicAnalyzeText = imageAnalyzeText
    .replace(/\\\*/g, '') // escaped *
    .replace(/(^|\n)\s*\*\s/g, ''); // list bullets

  const boldCount = (boldItalicAnalyzeText.match(/\*\*/g) || []).length;
  if (boldCount % 2) {
    closingSuffix = '**';
  }

  boldItalicAnalyzeText = boldItalicAnalyzeText.replace(/\*\*/g, ''); // bold
  const italicCount = (boldItalicAnalyzeText.match(/\*/g) || []).length;
  if (italicCount % 2) {
    closingSuffix = `*${closingSuffix}`;
  }

  return `${imageAnalyzeText}${closingSuffix}`;
}

export const Markdown = memo(
  ({
    className = null,
    text = null,
    bodyStream = null,
    customComponents = null,
    onLinkClick = noop,
    onRender = noop,
    isAnimationDisabled: isAnimationDisabledProp = false,
    isCanceled = false,
    messageId = null,
  }) => {
    const [isStreamComplete, setStreamComplete] = useState(false);
    const containerRef = useRef();
    const isAnimationDisabled = isAnimationDisabledProp || isStreamComplete;

    const onStreamComplete = useCallback(() => {
      setStreamComplete(true);
    }, []);

    const [responseText, stopResponse] = useResponseStream(null, {
      bodyStream,
      onComplete: onStreamComplete,
      creativeId: CANVAS_CREATIVE_ID,
    });

    const handleLinkClick = useCallback(
      event => {
        onLinkClick(event, messageId);
      },
      [messageId, onLinkClick]
    );

    const definedText = autoCloseMarkdown(text || responseText);

    const components = useMemo(() => {
      return {
        ...STRUCTURAL_TAGS.reduce((acc, tag) => {
          acc[tag] = ({ node, ...props }) => (
            <AnimationSchedule.MarkdownScheduledNode
              {...props}
              as={tag}
              unitId={getAnimationUnitId(node, tag)}
              isAnimationDisabled={isAnimationDisabled}
              reportAsRevealLeaf
            />
          );
          return acc;
        }, {}),
        ...WORD_BY_WORD_TAGS.reduce((acc, tag) => {
          acc[tag] = ({ node, ...props }) => {
            return (
              <WordByWordRenderer
                {...props}
                tag={tag}
                unitId={getAnimationUnitId(node, tag)}
                isAnimationDisabled={isAnimationDisabled}
              />
            );
          };
          return acc;
        }, {}),
        a({ node, children, ...restProps }) {
          if (customComponents?.a) {
            return (
              <AnimationSchedule.MarkdownScheduledNode
                {...restProps}
                as={customComponents.a}
                unitId={getAnimationUnitId(node, 'custom-a')}
                isAnimationDisabled={isAnimationDisabled}
                reportAsRevealLeaf
                onLinkClick={handleLinkClick}
              >
                {children}
              </AnimationSchedule.MarkdownScheduledNode>
            );
          }

          return (
            <MarkdownLinkRenderer
              {...restProps}
              unitId={getAnimationUnitId(node, 'link')}
              onLinkClick={handleLinkClick}
              isAnimationDisabled={isAnimationDisabled}
              target="_self"
              rel={null}
            >
              {children}
            </MarkdownLinkRenderer>
          );
        },
        ul({ node, ...restProps }) {
          return (
            <MarkdownList
              {...restProps}
              listType="ul"
              unitId={getAnimationUnitId(node, 'unordered-list')}
              isAnimationDisabled={isAnimationDisabled}
            />
          );
        },
        ol({ node, ...restProps }) {
          return (
            <MarkdownList
              {...restProps}
              listType="ol"
              unitId={getAnimationUnitId(node, 'ordered-list')}
              isAnimationDisabled={isAnimationDisabled}
            />
          );
        },
        li({ node, ...restProps }) {
          return (
            <MarkdownListItem
              {...restProps}
              unitId={getAnimationUnitId(node, 'list-item')}
              isAnimationDisabled={isAnimationDisabled}
            />
          );
        },
        tr({ node, ...restProps }) {
          return (
            <AnimationSchedule.MarkdownScheduledNode
              {...restProps}
              as="tr"
              unitId={getAnimationUnitId(node, 'table-row')}
              isAnimationDisabled={isAnimationDisabled}
              revealDelay={ANIMATION_GAP}
            />
          );
        },
        img({ node, ...restProps }) {
          return (
            <AnimationSchedule.MarkdownScheduledNode
              {...restProps}
              as={MarkdownImage}
              unitId={getAnimationUnitId(node, 'image')}
              isAnimationDisabled={isAnimationDisabled}
              reportAsRevealLeaf
            />
          );
        },
        ...Object.entries(omit(customComponents || {}, ['a'])).reduce((acc, [tag, component]) => {
          acc[tag] = ({ node, ...props }) => (
            <AnimationSchedule.MarkdownScheduledNode
              {...props}
              as={component}
              unitId={getAnimationUnitId(node, `custom-${tag}`)}
              isAnimationDisabled={isAnimationDisabled}
              reportAsRevealLeaf
            />
          );
          return acc;
        }, {}),
        table: ({ node, ...restProps }) => {
          return <MarkdownTable {...restProps} />;
        },
        blockquote({ node, ...restProps }) {
          return (
            <AnimationSchedule.MarkdownScheduledNode
              {...restProps}
              as="blockquote"
              unitId={getAnimationUnitId(node, 'blockquote')}
              isAnimationDisabled={isAnimationDisabled}
              className={classnames(restProps.className, 'pos-r ps-1 mb-1 fst-italic')}
            />
          );
        },
      };
    }, [customComponents, isAnimationDisabled, handleLinkClick]);

    useEffect(() => {
      if (definedText?.length) {
        onRender();
        EventToolbox.fireCustomEvent(PAGE_ASYNC_CONTENT_LOAD, { target: containerRef.current });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [!!definedText?.length]);

    useEffect(() => {
      if (isCanceled) {
        stopResponse();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCanceled]);

    if (!definedText) {
      return null;
    }

    return (
      <div ref={containerRef} className={classnames('ai-message-markdown text-cool-gray-10', className)}>
        <AnimationSchedule.RevealBoundary isAnimationDisabled={isAnimationDisabled}>
          <ReactMarkdown remarkPlugins={MARKDOWN_PLUGINS} components={components}>
            {definedText}
          </ReactMarkdown>
        </AnimationSchedule.RevealBoundary>
      </div>
    );
  }
);

Markdown.propTypes = {
  className: PropTypes.string,
  text: PropTypes.string,
  bodyStream: PropTypes.arrayOf(PropTypes.string),
  customComponents: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.func])),
  onLinkClick: PropTypes.func,
  messageId: PropTypes.string,
  onRender: PropTypes.func,
  isAnimationDisabled: PropTypes.bool,
  isCanceled: PropTypes.bool,
};
